// Country registry: the one place that knows which countries the app serves and how each
// behaves. New code never compares a country string literally; it goes through
// normalizeCountry()/areaCountry() and reads COUNTRIES. No imports at all: no React, router,
// Firebase or services.

/** The area types the maps API serves. */
export type MapType =
  | 'grid_250m'
  | 'grid_1000m'
  | 'admin_level_2'
  | 'admin_level_4'
  | 'osm_links'
  // Catalogue 1.0 (config/catalogue.ts): road links (maps.<country>_network_links, the ids the flows are keyed on),
  // the admin levels by depth, and the one model grid. The catalogue lists the levels, so they are open-ended.
  | 'network_links'
  | `admin_lvl_${number}`
  | `grid_${number}m`
  | 'model_grid'
  | 'station'
  | 'country';

export const COUNTRY_CODES = ['NOR', 'EST', 'LAT', 'LTU'] as const;
export type CountryCode = (typeof COUNTRY_CODES)[number];

/** The country the public demo API serves when the demo API is configured (see services/demoApi.ts). */
export const DEMO_API_COUNTRY: CountryCode = 'EST';
export type CountryAlias = 'NO' | 'EE' | 'LV' | 'LT';

export interface MapView {
  longitude: number;
  latitude: number;
  zoom: number;
}

/**
 * IANA zone in which one availability `batch_date` is one calendar day, per grain
 * (read-only BigQuery check, 9 Oct 2026):
 * - daily: every delivery stamps a daily bucket at TIMESTAMP(batch_date), i.e. the UTC day.
 * - hourly: NOR and EST batches are UTC days. LAT and LTU batches are a local day plus spill
 *   (28 buckets, D-1 21:00Z to D+1 00:00Z), so the local day is the window that matches.
 */
export interface DayZones {
  readonly daily: string;
  readonly hourly: string;
}

/** A coarse box, [west, south, east, north] in degrees. */
export type BBox = readonly [number, number, number, number];

export interface CountryDef {
  /** What we always SEND: /data-agg, /data-availability and /maps all accept the 3-letter code. */
  code: CountryCode;
  /** Accepted on input. Nominatim `countrycodes` = alias.toLowerCase(). */
  alias: CountryAlias;
  label: string;
  /** The emoji glyphs already used by AreaGroups. */
  flag: string;
  order: number;
  /** IANA zone of the country. Display only (v1 shows UTC). */
  timeZone: string;
  /** Zone in which one batch_date is one calendar day, per grain. See DayZones. */
  dayZone: DayZones;
  defaultView: MapView;
  /**
   * Coarse boxes that together roughly cover the country, [west, south, east, north]. Used only to
   * tell which country a drawn shape is in (see utils/countryForGeometry); not borders.
   */
  extent: readonly BBox[];
  /**
   * Geo types the maps API serves geometries for today; [] = none yet. Only used to tell which
   * country an old save without a country belongs to. The area picker does not read it: it shows
   * what /maps/list returns for the country.
   */
  mapGeoTypes: readonly MapType[];
  realtime: boolean;
  /** Served by the SSB open-data grids (population, workplaces). */
  ssbOpenData: boolean;
  roadLinks: boolean;
}

export const COUNTRIES: Readonly<Record<CountryCode, CountryDef>> = {
  NOR: {
    code: 'NOR',
    alias: 'NO',
    label: 'Norway',
    flag: '🇳🇴',
    order: 1,
    timeZone: 'Europe/Oslo',
    dayZone: { daily: 'UTC', hourly: 'UTC' },
    defaultView: { longitude: 10.757, latitude: 59.913, zoom: 10 },
    extent: [
      [4.5, 57.9, 12.0, 62.5],
      [4.5, 62.5, 13.0, 64.5],
      [9.5, 64.5, 15.5, 67.0],
      [12.0, 67.0, 19.5, 69.0],
      [14.5, 69.0, 31.2, 71.2],
    ],
    mapGeoTypes: ['admin_level_2', 'admin_level_4', 'grid_1000m', 'grid_250m', 'osm_links'],
    realtime: true,
    ssbOpenData: true,
    roadLinks: true,
  },
  EST: {
    code: 'EST',
    alias: 'EE',
    label: 'Estonia',
    flag: '🇪🇪',
    order: 2,
    timeZone: 'Europe/Tallinn',
    dayZone: { daily: 'UTC', hourly: 'UTC' },
    defaultView: { longitude: 24.745, latitude: 59.437, zoom: 12 },
    extent: [[21.7, 57.5, 28.3, 59.75]],
    mapGeoTypes: ['admin_level_2', 'admin_level_4', 'grid_1000m'],
    realtime: false,
    ssbOpenData: false,
    roadLinks: false,
  },
  LAT: {
    code: 'LAT',
    alias: 'LV',
    label: 'Latvia',
    flag: '🇱🇻',
    order: 3,
    timeZone: 'Europe/Riga',
    dayZone: { daily: 'UTC', hourly: 'Europe/Riga' },
    defaultView: { longitude: 24.1056, latitude: 56.9496, zoom: 11 },
    extent: [[20.9, 55.65, 28.3, 58.1]],
    mapGeoTypes: [],
    realtime: false,
    ssbOpenData: false,
    roadLinks: false,
  },
  LTU: {
    code: 'LTU',
    alias: 'LT',
    label: 'Lithuania',
    flag: '🇱🇹',
    order: 4,
    timeZone: 'Europe/Vilnius',
    dayZone: { daily: 'UTC', hourly: 'Europe/Vilnius' },
    defaultView: { longitude: 25.2797, latitude: 54.6872, zoom: 11 },
    extent: [[20.9, 53.88, 26.9, 56.45]],
    mapGeoTypes: [],
    realtime: false,
    ssbOpenData: false,
    roadLinks: false,
  },
};

export const COUNTRY_LIST: readonly CountryDef[] = COUNTRY_CODES.map((c) => COUNTRIES[c]);

/** The country a new area starts in, and the fallback wherever one is needed: the first in the registry. */
export const DEFAULT_COUNTRY: CountryCode = COUNTRY_LIST[0].code;

/**
 * The country Real Time serves: the first registry country with `realtime`. Real Time polygons
 * saved without a country are shown as belonging to it. Display only; nothing is stored.
 */
export function realtimeDefaultCountry(): CountryCode {
  return COUNTRY_LIST.find((c) => c.realtime)?.code ?? DEFAULT_COUNTRY;
}

/** Road-link area types. Only countries with `roadLinks` serve them. */
export const ROAD_LINK_TYPES: readonly string[] = ['osm_links', 'network_links', 'statens_vegvesen'];

/** The group key for areas that have no usable country. */
export const UNASSIGNED = 'unassigned' as const;
export type AreaCountryKey = CountryCode | typeof UNASSIGNED;

const ALIAS_TO_CODE: Readonly<Record<CountryAlias, CountryCode>> = {
  NO: 'NOR',
  EE: 'EST',
  LV: 'LAT',
  LT: 'LTU',
};

const isCountryCode = (s: string): s is CountryCode => (COUNTRY_CODES as readonly string[]).includes(s);
const isCountryAlias = (s: string): s is CountryAlias => Object.prototype.hasOwnProperty.call(ALIAS_TO_CODE, s);

/**
 * What the users API stamps with NOR when a save carries no country (users-api
 * COUNTRY_CODE_AWARE_MAP_TYPES). Areas of these types saved without a country are Norwegian.
 */
const BACKEND_DEFAULTS_TO_NOR: readonly string[] = ['grid_1000m', 'admin_level_2', 'admin_level_4'];

/** The country the backend stamps on the area types in BACKEND_DEFAULTS_TO_NOR. Used by areaCountry only. */
export const LEGACY_DEFAULT_COUNTRY: CountryCode = 'NOR';

/** 'nor' | ' NO ' | 'EE' | 'EST' | 'lv' | 'LTU' ... -> code; anything else -> null. Never defaults. */
export function normalizeCountry(input: unknown): CountryCode | null {
  if (typeof input !== 'string') return null;
  const s = input.trim().toUpperCase();
  if (isCountryCode(s)) return s;
  if (isCountryAlias(s)) return ALIAS_TO_CODE[s];
  return null;
}

/**
 * The country a saved area belongs to, or null when it cannot be told. In order:
 * 1. A saved country code decides. An unknown code (such as 'XX') gives null; it never becomes Norway.
 * 2. With no code, the area type decides when the backend would have stamped a country: grid and
 *    admin saves without a country are Norwegian.
 * 3. With no code, a type that exactly one registry country serves (250 m grid, OSM and Statens
 *    vegvesen road links) belongs to that country.
 * 4. Anything else (custom polygons, unknown types, an empty object) gives null. The Library shows
 *    those under "No country set".
 */
export function areaCountry(area: { country_code?: string | null; type?: string | null }): CountryCode | null {
  const code = typeof area.country_code === 'string' ? area.country_code.trim() : '';
  if (code !== '') return normalizeCountry(code);

  const type = typeof area.type === 'string' ? area.type : '';
  if (type === '') return null;
  if (BACKEND_DEFAULTS_TO_NOR.includes(type)) return LEGACY_DEFAULT_COUNTRY;

  const servedBy = COUNTRY_LIST.filter(
    (c) => (c.mapGeoTypes as readonly string[]).includes(type) || (c.roadLinks && ROAD_LINK_TYPES.includes(type)),
  );
  return servedBy.length === 1 ? servedBy[0].code : null;
}

/**
 * Countries offered on the standard polygon page: every registry country. What a country offers
 * there comes from /maps/list, so a country with no maps yet is still selectable and says so.
 */
export function mapCountries(): readonly CountryDef[] {
  return COUNTRY_LIST;
}

/** The country whose road links the road-link editor serves: the first with `roadLinks`, else the default. */
export function roadLinksCountry(): CountryCode {
  return COUNTRY_LIST.find((c) => c.roadLinks)?.code ?? DEFAULT_COUNTRY;
}

// Some older saves carry the wrong area type, and the shape of their ids says which one it is.
// The rules are per country: other countries keep the type they were saved with.
const LEGACY_AREA_TYPE_BY_ID: Readonly<Partial<Record<CountryCode, (id: string) => MapType | null>>> = {
  NOR: (id) => {
    if (/^3\d{6}$/.test(id)) return 'admin_level_4'; // 7 digits starting with 3: a basic statistical unit
    if (/^\d{4}$/.test(id)) return 'admin_level_2'; // 4 digits: a municipality
    return null;
  },
};

/** The area type an older save's id implies in this country, or null when there is no rule or no match. */
export function inferLegacyAreaType(country: CountryCode, id: string): MapType | null {
  return LEGACY_AREA_TYPE_BY_ID[country]?.(id) ?? null;
}

const UNKNOWN_FLAG = '🌍';

/** How the group of areas with no usable country is shown. */
export const NO_COUNTRY = { label: 'No country set', flag: UNKNOWN_FLAG } as const;

/**
 * Display data for a possibly-unknown code. Known codes and aliases resolve to the registry
 * entry; anything else comes back as itself with the globe flag.
 */
export function countryMeta(code: string | null | undefined): { code: string; label: string; flag: string } {
  const known = normalizeCountry(code);
  if (known) {
    const { label, flag } = COUNTRIES[known];
    return { code: known, label, flag };
  }
  const raw = typeof code === 'string' ? code.trim() : '';
  return { code: raw, label: raw || 'Unknown country', flag: UNKNOWN_FLAG };
}
